/*
 * Copyright (c) 2024. Tooling Team
 */

import { IDENTITY_CONFIG, METADATA_OIDC } from "../utils/authConfig";
import { Log, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { PATHS } from "../utils/constants";
import HeaderService from "../services/headerService";
import { store } from "../app/store";
import { userApi } from "../app/services/userApi";
import {
  beginInteractiveSignin,
  recoverSession,
  registerAuthRecoveryHandlers,
} from "./authRecoveryService";

export default class AuthService {
  UserManager;
  headerService;

  constructor() {
    this.headerService = new HeaderService();
    // oidc-client-ts client for the Keycloak (OIDC) provider. Users/tokens are
    // kept in sessionStorage, so they last only for the browser tab's lifetime.
    this.UserManager = new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      metadata: {
        ...METADATA_OIDC,
      },
    });
    // Logger
    Log.logger = console;
    Log.level = Log.DEBUG;
    // Fires whenever a user is loaded into the store (sign-in or silent renew)
    this.UserManager.events.addUserLoaded(() => {
      if (window.location.href.indexOf("signin-oidc") !== -1) {
        this.navigateToScreen();
      }
    });
    // Fires when a silent token renewal fails; fall back to interactive sign-in
    this.UserManager.events.addSilentRenewError((e) => {
      console.log("silent renew error", e.message);
      beginInteractiveSignin();
    });
    // Fires when the access token expires; try to recover the session
    this.UserManager.events.addAccessTokenExpired(() => {
      recoverSession();
    });
    registerAuthRecoveryHandlers({
      renewSession: this.signinSilent,
      clearSession: this.cleanSessionStorage,
      signinRedirect: this.signinRedirect,
    });
  }

  //clean session storage on log out
  cleanSessionStorage = () => {
    sessionStorage.removeItem(
      `oidc.user:${process.env.REACT_APP_AUTH_URL}:${process.env.REACT_APP_IDENTITY_CLIENT_ID}`
    );
    sessionStorage.removeItem(`isAdmin`);
    sessionStorage.removeItem(`userRoles`);
  };

  signinRedirectCallback = () => {
    // Runs on /callback after Keycloak redirects back with an auth code:
    // validates the response state, exchanges the code for tokens and stores the user
    this.UserManager.signinRedirectCallback().then(async () => {
      if (process.env.REACT_APP_DEVELOPMENT_AUTH_DISABLED === "true") {
        sessionStorage.setItem("isAdmin", "true");
      } else {
        await this.addUserProfileToSession();
      }
      window.location.replace("/"); //redirect to main page for roles and other info to be loaded before user can navigate the app
    });
  };

  getUser = async () => {
    // Reads the current user from the store (null if not signed in)
    const user = await this.UserManager.getUser();
    if (!user) {
      // No stored user: try to complete a sign-in from the current URL's
      // callback params (rejects if the URL isn't a sign-in response)
      return await this.UserManager.signinRedirectCallback();
    }
    return user;
  };

  parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  };

  signinRedirect = () => {
    // Sends the browser to Keycloak's authorization endpoint to sign in
    this.UserManager.signinRedirect({});
  };

  signoutRedirect = (idToken) => {
    // Sends the browser to Keycloak's end_session_endpoint (with id_token_hint)
    // to end the IdP session. Keycloak then returns to post_logout_redirect_uri
    // (REACT_APP_LOGOFF_REDIRECT_URL = /logout/callback)
    return this.UserManager.signoutRedirect(
      idToken ? { id_token_hint: idToken } : undefined
    );
  };

  navigateToScreen = () => {
    window.location.replace("/en/dashboard");
  };

  isAuthenticated = () => {
    const oidcStorage = JSON.parse(
      sessionStorage.getItem(
        `oidc.user:${process.env.REACT_APP_AUTH_URL}:${process.env.REACT_APP_IDENTITY_CLIENT_ID}`
      )
    );
    return !!oidcStorage && !!oidcStorage.access_token;
  };

  signinSilent = async () => {
    // Renews tokens in a hidden iframe via the silent_redirect_uri, without
    // a full-page redirect
    const user = await this.UserManager.signinSilent();
    console.log("signed in", user);
    return user;
  };
  signinSilentCallback = () => {
    // Runs inside the silent-renew iframe (/silentrenew): passes the renewal
    // response back to the parent window's UserManager
    this.UserManager.signinSilentCallback();
  };

  createSigninRequest = () => {
    // Builds the sign-in request (authorization URL + saved state) without navigating
    return this.UserManager.createSigninRequest();
  };

  logout = async () => {
    // signoutRedirect loads the stored user asynchronously (after the navigator
    // is prepared), so read the id token first and pass it explicitly; the
    // session cleanup below would otherwise remove the user before it is read.
    // localStorage holds the signout state that /logout/callback must match, so
    // it is cleared in signoutRedirectCallback instead.
    const user = await this.UserManager.getUser();
    const redirect = this.signoutRedirect(user?.id_token);
    this.cleanSessionStorage();
    await redirect;
  };

  // Runs on /logout/callback after the IdP has ended the session
  signoutRedirectCallback = async () => {
    try {
      // Completes the signout handshake: reads `state` from the callback URL,
      // matches and removes the saved signout state, and rejects on an IdP
      // error or unknown state. It does not end the IdP session or redirect.
      await this.UserManager.signoutRedirectCallback();
    } finally {
      localStorage.clear();
      // Removes any leftover expired state entries from the store
      this.UserManager.clearStaleState();
      window.location.replace("/loggedout");
    }
  };

  addUserProfileToSession = async () => {
    const response = await store.dispatch(
      userApi.endpoints.getUserProfile.initiate()
    );
    if (response.status === 200) {
      const data = await response.json();
      sessionStorage.setItem("isAdmin", `${data.isAdmin === true}`);
    }
    if (response.status === 403) {
      window.location.replace(PATHS.forbidden.url);
    }
  };
}
